import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static values = {
        notice: String,
        alert: String,
        modal: String, // optional blocking message
    }

    connect() {
        // Show toasts
        if (this.hasNoticeValue && this.noticeValue.trim() !== "") {
            this.toast(this.noticeValue, "notice")
        }
        if (this.hasAlertValue && this.alertValue.trim() !== "") {
            this.toast(this.alertValue, "alert")
        }

        // Optional blocking modal (only if set flash[:modal])
        if (this.hasModalValue && this.modalValue.trim() !== "") {
            this.modal(this.modalValue)
        }

        // Prevent repeat display if this element survives Turbo caching
        this.element.removeAttribute("data-flash-notice-value")
        this.element.removeAttribute("data-flash-alert-value")
        this.element.removeAttribute("data-flash-modal-value")
    }

    toast(message, type = "notice") {
        const container = this.ensureToastContainer()
        const iconClass = document.body.classList.contains("admin-body")
            ? "material-symbols-rounded"
            : "material-icons-outlined"

        const el = document.createElement("div")
        el.setAttribute("role", "status")
        el.className = [
            "pointer-events-auto w-full max-w-sm rounded-2xl border shadow-lg",
            "px-4 py-3 flex items-start gap-3",
            "bg-white border-slate-200 text-slate-900",
            "dark:bg-slate-900 dark:border-slate-700 dark:text-white",
            "animate-[flash-in_180ms_ease-out]",
        ].join(" ")

        const icon = document.createElement("span")
        icon.className = [
            `${iconClass} mt-[1px]`,
            type === "alert" ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400",
        ].join(" ")
        icon.textContent = type === "alert" ? "error" : "check_circle"

        const text = document.createElement("div")
        text.className = "text-sm font-semibold leading-snug"
        text.textContent = message // IMPORTANT: textContent = XSS-safe

        const close = document.createElement("button")
        close.type = "button"
        close.className = "ml-auto text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        close.innerHTML = `<span class="${iconClass} text-[18px]">close</span>`
        close.addEventListener("click", () => this.dismissToast(el))

        el.appendChild(icon)
        el.appendChild(text)
        el.appendChild(close)

        container.appendChild(el)

        // auto-dismiss
        const ms = type === "alert" ? 6000 : 3500
        window.setTimeout(() => this.dismissToast(el), ms)
    }

    dismissToast(el) {
        if (!el || !el.isConnected) return
        el.classList.remove("animate-[flash-in_180ms_ease-out]")
        el.classList.add("animate-[flash-out_180ms_ease-in]")
        window.setTimeout(() => el.remove(), 180)
    }

    ensureToastContainer() {
        let container = document.getElementById("flash-toasts")
        if (container) return container

        container = document.createElement("div")
        container.id = "flash-toasts"
        container.className = [
            "fixed z-[100] top-4 right-4",
            "flex flex-col gap-3 items-end",
            "w-[calc(100vw-2rem)] sm:w-auto",
            "pointer-events-none",
        ].join(" ")
        document.body.appendChild(container)

        // keyframes (scoped, no Tailwind config needed)
        if (!document.getElementById("flash-keyframes")) {
            const style = document.createElement("style")
            style.id = "flash-keyframes"
            style.textContent = `
        @keyframes flash-in { from { opacity: 0; transform: translateY(-6px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes flash-out { from { opacity: 1; transform: translateY(0) scale(1); } to { opacity: 0; transform: translateY(-6px) scale(.98); } }
      `
            document.head.appendChild(style)
        }

        return container
    }

    modal(message) {
        // Simple Tailwind modal (no dependency)
        const backdrop = document.createElement("div")
        backdrop.className = "fixed inset-0 z-[200] bg-black/40 backdrop-blur-[1px] flex items-center justify-center p-4"

        const card = document.createElement("div")
        card.className = [
            "w-full max-w-lg rounded-2xl bg-white border border-slate-200 shadow-2xl",
            "dark:bg-slate-900 dark:border-slate-700",
            "p-6",
        ].join(" ")

        const title = document.createElement("div")
        title.className = "text-lg font-extrabold text-slate-900 dark:text-white mb-2"
        title.textContent = "Notice"

        const body = document.createElement("div")
        body.className = "text-sm text-slate-600 dark:text-slate-300 leading-relaxed"
        body.textContent = message

        const actions = document.createElement("div")
        actions.className = "mt-5 flex justify-end gap-3"

        const btn = document.createElement("button")
        btn.type = "button"
        btn.className = "px-5 py-2.5 rounded-xl bg-primary text-white font-bold text-sm"
        btn.textContent = "OK"
        btn.addEventListener("click", () => backdrop.remove())

        backdrop.addEventListener("click", (e) => {
            if (e.target === backdrop) backdrop.remove()
        })

        actions.appendChild(btn)
        card.appendChild(title)
        card.appendChild(body)
        card.appendChild(actions)
        backdrop.appendChild(card)
        document.body.appendChild(backdrop)
    }
}
